/**
 * Lightweight user-agent device detection for routing decisions (e.g. the
 * activation smart link choosing App Store vs Play Store vs web). Best-effort
 * only — never gate critical behaviour on these.
 */

export const isAndroidUA = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android/i.test(window.navigator.userAgent || '');
};

export const isIOSUA = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  // iPadOS 13+ reports as desktop Safari but exposes touch points.
  return /Macintosh/i.test(ua) && (window.navigator.maxTouchPoints || 0) > 1;
};

export type MobilePlatform = 'ios' | 'android' | 'other';

export const getMobilePlatform = (): MobilePlatform => {
  if (isIOSUA()) return 'ios';
  if (isAndroidUA()) return 'android';
  return 'other';
};

/**
 * True when the current page is being hosted inside another app/site rather
 * than visited directly on helpgenie.ai: `?layout=full` (e.g. Gulf Western's
 * MAXX app framing a genie full-screen), any `?embed=` query param, or the
 * page running inside an iframe at all. Used to suppress chrome that only
 * makes sense for a direct visitor (e.g. the "install our app" banner),
 * which would otherwise sit on top of a host app's own UI.
 */
export const isEmbeddedContext = (): boolean => {
  if (typeof window === 'undefined') return false;

  const params = new URLSearchParams(window.location.search);
  if (params.get('layout') === 'full') return true;
  if (params.get('embed') === 'true') return true;

  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin sandboxing can throw on window.top access — treat as embedded.
    return true;
  }
};
